// ============================================================================
// /f/calificar/:token — página PÚBLICA (sin login) donde el comensal califica
// su experiencia tras escanear el QR post-pago.
//
// Flujo: 3 preguntas básicas con estrellas (atención, comida, recomendación)
// → puede enviar ya, o tocar "Quiero contar más" y responder las opcionales
// (velocidad, limpieza, ambiente + comentario). Un solo envío por venta.
// Mobile-first: el 100% de los escaneos vienen de un celular.
// ============================================================================
import { useEffect, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router';
import { Star, Check, Loader2 } from 'lucide-react';
import { getPublicCalificacion, enviarCalificacion } from '../../api/calificaciones';

const card: CSSProperties = {
  background: 'var(--surface, #fff)',
  border: '1.5px solid var(--line, #e2e8f0)',
  borderRadius: 18,
  padding: 20,
};

// --- Estrellas 1-5 ---------------------------------------------------------
function Estrellas({ value, onChange, disabled }: {
  value: number | null;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex gap-1.5" role="radiogroup">
      {[1, 2, 3, 4, 5].map(n => {
        const activa = value != null && n <= value;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} de 5`}
            disabled={disabled}
            onClick={() => onChange(n)}
            className="press"
            style={{ background: 'transparent', border: 0, cursor: 'pointer', padding: 4 }}
          >
            <Star
              className="w-9 h-9"
              style={{
                color: activa ? '#E6A100' : 'var(--line-2, #cbd5e1)',
                fill: activa ? '#E6A100' : 'transparent',
                transition: 'color .12s, fill .12s',
              }}
            />
          </button>
        );
      })}
    </div>
  );
}

function Pregunta({ titulo, value, onChange, disabled }: {
  titulo: string;
  value: number | null;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <div style={card}>
      <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink, #0f172a)', marginBottom: 10 }}>
        {titulo}
      </div>
      <Estrellas value={value} onChange={onChange} disabled={disabled} />
    </div>
  );
}

export function PublicCalificacionForm() {
  const { token = '' } = useParams();
  const [fase, setFase] = useState<'cargando' | 'form' | 'ya-respondida' | 'no-existe' | 'enviando' | 'gracias'>('cargando');
  const [sede, setSede] = useState('Salonika');
  const [error, setError] = useState<string | null>(null);

  // Básicas (obligatorias)
  const [atencion, setAtencion] = useState<number | null>(null);
  const [comida, setComida] = useState<number | null>(null);
  const [recomendacion, setRecomendacion] = useState<number | null>(null);
  // Extras (opcionales, se muestran si el comensal quiere contar más)
  const [masPreguntas, setMasPreguntas] = useState(false);
  const [velocidad, setVelocidad] = useState<number | null>(null);
  const [limpieza, setLimpieza] = useState<number | null>(null);
  const [ambiente, setAmbiente] = useState<number | null>(null);
  const [comentario, setComentario] = useState('');

  useEffect(() => {
    getPublicCalificacion(token)
      .then(r => {
        setSede(r.sede_nombre || 'Salonika');
        setFase(r.estado === 'pendiente' ? 'form' : 'ya-respondida');
      })
      .catch((e: any) => setFase(e?.status === 404 ? 'no-existe' : 'no-existe'));
  }, [token]);

  const basicasCompletas = atencion != null && comida != null && recomendacion != null;

  const enviar = async () => {
    if (!basicasCompletas) return;
    setFase('enviando');
    setError(null);
    try {
      await enviarCalificacion(token, {
        q_atencion: atencion!,
        q_comida: comida!,
        q_recomendacion: recomendacion!,
        q_velocidad: velocidad,
        q_limpieza: limpieza,
        q_ambiente: ambiente,
        comentario: comentario.trim() || null,
      });
      setFase('gracias');
    } catch (e: any) {
      if (e?.status === 409) { setFase('ya-respondida'); return; }
      setError(e?.message || 'No se pudo enviar. Intentá de nuevo.');
      setFase('form');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app, #f1f5f9)', padding: '24px 16px' }}>
      <div style={{ maxWidth: 460, margin: '0 auto' }} className="space-y-4">
        {/* Header */}
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--blue-700, #0D5EAF)' }}>{sede}</div>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--muted-ink, #64748b)', marginTop: 4 }}>
            ¿Cómo fue tu experiencia? Nos toma menos de un minuto.
          </div>
        </div>

        {fase === 'cargando' && (
          <div style={{ ...card, textAlign: 'center', padding: 40 }}>
            <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: 'var(--blue-600, #1f4fd8)' }} />
          </div>
        )}

        {fase === 'no-existe' && (
          <div style={{ ...card, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink, #0f172a)' }}>
              Este enlace de calificación no existe o expiró.
            </div>
          </div>
        )}

        {fase === 'ya-respondida' && (
          <div style={{ ...card, textAlign: 'center' }}>
            <Check className="w-10 h-10 mx-auto mb-2" style={{ color: '#047857' }} />
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink, #0f172a)' }}>
              Esta visita ya fue calificada. ¡Gracias!
            </div>
          </div>
        )}

        {fase === 'gracias' && (
          <div style={{ ...card, textAlign: 'center', padding: 32 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', margin: '0 auto 12px',
              background: 'rgba(4,120,87,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Check className="w-8 h-8" style={{ color: '#047857' }} />
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--ink, #0f172a)' }}>¡Gracias por tu opinión!</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--muted-ink, #64748b)', marginTop: 6 }}>
              Nos ayuda a atenderte mejor la próxima vez.
            </div>
          </div>
        )}

        {(fase === 'form' || fase === 'enviando') && (
          <>
            {error && (
              <div style={{ ...card, borderColor: 'rgba(220,38,38,.4)', color: '#b91c1c', fontSize: 13.5, fontWeight: 700 }}>
                {error}
              </div>
            )}

            <Pregunta titulo="¿Cómo fue la atención de quien te atendió?" value={atencion} onChange={setAtencion} disabled={fase === 'enviando'} />
            <Pregunta titulo="¿Qué tal estuvo la comida?" value={comida} onChange={setComida} disabled={fase === 'enviando'} />
            <Pregunta titulo="¿Nos recomendarías a un amigo?" value={recomendacion} onChange={setRecomendacion} disabled={fase === 'enviando'} />

            {/* Extras opcionales */}
            {!masPreguntas ? (
              <button
                type="button"
                onClick={() => setMasPreguntas(true)}
                style={{
                  width: '100%', padding: '12px 16px', borderRadius: 14, cursor: 'pointer',
                  border: '1.5px dashed var(--line-2, #cbd5e1)', background: 'transparent',
                  color: 'var(--muted-ink, #64748b)', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
                }}
              >
                + Quiero contar más (opcional)
              </button>
            ) : (
              <>
                <Pregunta titulo="¿Qué tan rápido te atendimos?" value={velocidad} onChange={setVelocidad} disabled={fase === 'enviando'} />
                <Pregunta titulo="¿Cómo viste la limpieza del lugar?" value={limpieza} onChange={setLimpieza} disabled={fase === 'enviando'} />
                <Pregunta titulo="¿Qué tal el ambiente?" value={ambiente} onChange={setAmbiente} disabled={fase === 'enviando'} />
                <div style={card}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink, #0f172a)', marginBottom: 10 }}>
                    ¿Algo más que quieras contarnos?
                  </div>
                  <textarea
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value.slice(0, 1000))}
                    rows={3}
                    disabled={fase === 'enviando'}
                    placeholder="Tu comentario (opcional)…"
                    style={{
                      width: '100%', padding: 12, borderRadius: 12, resize: 'none',
                      border: '1.5px solid var(--line-2, #cbd5e1)', fontFamily: 'inherit',
                      fontSize: 14.5, fontWeight: 500, color: 'var(--ink, #0f172a)',
                      background: 'var(--surface, #fff)', outline: 'none',
                    }}
                  />
                </div>
              </>
            )}

            <button
              type="button"
              onClick={enviar}
              disabled={!basicasCompletas || fase === 'enviando'}
              style={{
                width: '100%', padding: '15px 16px', borderRadius: 14, border: 0,
                background: basicasCompletas ? 'var(--blue-600, #1f4fd8)' : 'var(--line-2, #cbd5e1)',
                color: '#fff', fontSize: 16, fontWeight: 800, fontFamily: 'inherit',
                cursor: basicasCompletas ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {fase === 'enviando'
                ? <><Loader2 className="w-5 h-5 animate-spin" /> Enviando…</>
                : 'Enviar calificación'}
            </button>
            {!basicasCompletas && (
              <div style={{ textAlign: 'center', fontSize: 12.5, fontWeight: 600, color: 'var(--muted-ink, #64748b)' }}>
                Respondé las 3 preguntas con estrellas para enviar.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
